import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

export default function FoundersLetter() {
  useSEO({
    title: "A Letter from the Founders — Tandem T Group",
    description: "A letter from Chrissy Thompson and Rohan Taylor on the founding of Tandem T Holdings, Tandem T Logistics, Tandem T Properties, and The Tandem T Foundation.",
    path: "/letter",
  });
  return (
    <div data-testid="page-letter" className="pt-32 md:pt-40 pb-24">
      {/* HEADER */}
      <section className="border-b border-white/10 pb-20 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="overline text-white/55">From the Founders</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-serif font-light text-white text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight mt-8">
              A Letter from the <em className="italic">Founders.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="w-16 h-px bg-white/40 mx-auto mt-12" />
          </Reveal>

          <Reveal delay={0.24}>
            <a
              href="#mark"
              data-testid="letter-teaser"
              className="group inline-flex flex-col items-center gap-3 mt-14 hover:opacity-80 transition-opacity"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("mark")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              <p className="font-serif italic text-white/70 text-lg md:text-xl font-light leading-snug max-w-xl text-center">
                Read to the end &mdash; discover <em className="text-white">the meaning behind our mark.</em>
              </p>
              <span className="overline text-white/45 group-hover:text-white transition-colors">
                Continue Below
              </span>
              <ChevronDown
                size={20}
                className="text-white/45 group-hover:text-white transition-colors animate-bounce"
              />
            </a>
          </Reveal>
        </div>
      </section>

      {/* LETTER BODY — editorial single column */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* subtle watermark */}
        <div className="absolute left-1/2 top-[8%] -translate-x-1/2 w-[70%] max-w-[720px] opacity-[0.04] pointer-events-none select-none" aria-hidden>
          <img src="/brand/tandem-t-logo.png" alt="" className="w-full h-auto" style={{ mixBlendMode: "screen" }} />
        </div>

        <div className="relative max-w-[820px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="font-serif italic text-white text-2xl md:text-3xl font-light leading-snug">
              Every great organization begins with an idea. Ours began with a{" "}
              <strong className="font-semibold not-italic text-white">partnership.</strong>
            </p>
          </Reveal>

          <Reveal delay={0.06}>
            <p className="text-white/80 font-light leading-[1.9] text-lg md:text-xl mt-10">
              The name <span className="text-white">Tandem T</span> represents more than two initials. It
              represents two people who believed they could{" "}
              <strong className="font-semibold text-white">accomplish more together than apart.</strong>{" "}
              That belief became the foundation of everything we have built &mdash; and everything we will
              continue to build.
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <p className="text-white/80 font-light leading-[1.9] text-lg md:text-xl mt-8">
              From the beginning, our vision was never simply to own companies. It was to create an
              organization that brings{" "}
              <strong className="font-semibold text-white">people, businesses, and communities together</strong>{" "}
              to solve meaningful problems through{" "}
              <strong className="font-semibold text-white">trusted partnerships.</strong>
            </p>
          </Reveal>

          {/* three beliefs — editorial block */}
          <Reveal delay={0.1}>
            <div className="mt-14 border-t border-b border-white/15 py-10 md:py-14 space-y-6">
              <p className="font-serif text-white text-2xl md:text-3xl font-light leading-snug">
                We believe business has the power to create <em className="italic">opportunity.</em>
              </p>
              <p className="font-serif text-white text-2xl md:text-3xl font-light leading-snug">
                We believe long-term success is built on <em className="italic">trust, integrity, and relationships.</em>
              </p>
              <p className="font-serif text-white text-2xl md:text-3xl font-light leading-snug">
                We believe every investment should strengthen a community, every property should create
                stability, every delivery should connect opportunity, and every partnership should leave
                something <em className="italic">better than it found it.</em>
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="text-white/80 font-light leading-[1.9] text-lg md:text-xl mt-14">
              The Tandem T Group is more than a portfolio of companies. It is a commitment to{" "}
              <strong className="font-semibold text-white">responsible leadership, purposeful growth, and lasting impact.</strong>{" "}
              Whether we are investing through <span className="text-white">Tandem T Holdings</span>,
              connecting communities through <span className="text-white">Tandem T Logistics</span>,
              creating places through <span className="text-white">Tandem T Properties</span>, or expanding
              opportunity through <span className="text-white">The Tandem T Foundation</span>, our purpose
              remains the same.
            </p>
          </Reveal>

          <Reveal delay={0.14}>
            <p className="text-white/80 font-light leading-[1.9] text-lg md:text-xl mt-8">
              We are building something that will{" "}
              <strong className="font-semibold text-white">endure</strong> &mdash; not because of the
              buildings we develop or the businesses we acquire, but because of the{" "}
              <strong className="font-semibold text-white">trust we earn</strong>, the{" "}
              <strong className="font-semibold text-white">partnerships we cultivate</strong>, and the{" "}
              <strong className="font-semibold text-white">communities we strengthen together.</strong>
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-16 text-center space-y-3">
              <p className="font-serif italic text-white text-xl md:text-2xl font-light leading-snug">
                This is our promise.
              </p>
              <p className="font-serif italic text-white text-xl md:text-2xl font-light leading-snug">
                This is our purpose.
              </p>
              <p className="font-serif italic text-white text-xl md:text-2xl font-light leading-snug">
                This is <span className="text-white">The Tandem T Group.</span>
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.24}>
            <div className="mt-20 pt-10 border-t border-white/15 text-center">
              <p className="overline text-white/45">In Tandem</p>
              <div className="mt-6 space-y-2">
                <p className="font-serif text-white text-3xl md:text-4xl font-light">Chrissy Thompson</p>
                <p className="overline text-white/60">President & Chief Executive Officer</p>
              </div>
              <div className="mt-8 space-y-2">
                <p className="font-serif text-white text-3xl md:text-4xl font-light">Rohan Taylor</p>
                <p className="overline text-white/60">Chief Operating Officer</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE MEANING BEHIND OUR MARK */}
      <section id="mark" className="relative border-t border-white/10 py-28 md:py-40 overflow-hidden scroll-mt-24" data-testid="letter-mark">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="overline text-white/55">The Tandem T Group</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl leading-[1.05] tracking-tight mt-6">
              Building <em className="italic">Partnerships.</em> Creating <em className="italic">Opportunity.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="w-16 h-px bg-white/40 mx-auto mt-12" />
          </Reveal>

          {/* Prominent mark */}
          <Reveal delay={0.2}>
            <div className="mt-14 flex justify-center">
              <img
                src="/brand/tandem-t-logo.png"
                alt="The Tandem T Mark"
                className="w-64 md:w-96 h-auto object-contain"
                style={{ mixBlendMode: "screen", filter: "brightness(1.35) contrast(1.1)" }}
              />
            </div>
          </Reveal>

          <Reveal delay={0.28}>
            <p className="overline text-white/55 mt-16">The Meaning Behind Our Mark</p>
          </Reveal>
        </div>

        {/* Three elements */}
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {[
            {
              label: "The Monogram",
              body: (
                <>
                  Represents the <strong className="font-semibold text-white">partnership</strong> on which
                  our organization was founded &mdash;{" "}
                  <strong className="font-semibold text-white">Thompson and Taylor</strong> working in tandem.
                </>
              ),
            },
            {
              label: "The Compass Star",
              body: (
                <>
                  Symbolizes <strong className="font-semibold text-white">vision, integrity,</strong> and{" "}
                  <strong className="font-semibold text-white">purposeful leadership.</strong>
                </>
              ),
            },
            {
              label: "The Laurel Wreath",
              body: (
                <>
                  Reflects <strong className="font-semibold text-white">achievement</strong> earned through{" "}
                  <strong className="font-semibold text-white">trust</strong> and{" "}
                  <strong className="font-semibold text-white">service.</strong>
                </>
              ),
            },
          ].map((e, i) => (
            <Reveal key={e.label} delay={i * 0.08} className="bg-black">
              <div className="p-8 md:p-10 min-h-[220px] flex flex-col justify-center text-center">
                <p className="overline text-white/45">0{i + 1}</p>
                <p className="font-serif text-white text-2xl md:text-3xl font-light mt-5 leading-tight">{e.label}</p>
                <div className="w-10 h-px bg-white/25 mx-auto my-5" />
                <p className="text-white/70 font-light leading-relaxed text-sm md:text-base">{e.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="max-w-[900px] mx-auto px-6 md:px-10 mt-16 text-center">
          <Reveal delay={0.16}>
            <p className="font-serif italic text-white/85 text-xl md:text-2xl font-light leading-snug">
              Together, these elements represent our commitment to{" "}
              <strong className="font-semibold not-italic text-white">building partnerships</strong> that
              create <strong className="font-semibold not-italic text-white">opportunity</strong> and{" "}
              <strong className="font-semibold not-italic text-white">lasting value.</strong>
            </p>
          </Reveal>
        </div>
      </section>

      {/* CLOSING BANNER */}
      <section className="border-t border-white/10 py-24 md:py-32" data-testid="letter-closing">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {[
            "Together, We Build Trust.",
            "Together, We Create Opportunity.",
            "Together, We Strengthen Communities.",
          ].map((line, i) => (
            <Reveal key={line} delay={i * 0.08} className="bg-black">
              <div className="p-10 min-h-[200px] flex items-center">
                <p className="font-serif italic text-white text-2xl md:text-3xl font-light leading-snug">
                  {line}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Link
            to="/contact"
            data-testid="letter-cta-contact"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-white text-black overline hover:bg-white/85 transition-colors"
          >
            Partner With Tandem T <ArrowUpRight size={16} className="hover-arrow" />
          </Link>
          <Link
            to="/"
            data-testid="letter-cta-home"
            className="overline text-white/55 hover:text-white transition-colors"
          >
            ← Return to Tandem T Holdings
          </Link>
        </div>
      </section>
    </div>
  );
}
